import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Lightbulb,
  MapPin,
  TrendingUp,
  TrendingDown,
  MinusCircle,
  Calendar,
  AlertTriangle,
  Thermometer,
  BarChart3,
  Info,
  Search,
  Zap
} from "lucide-react";
import axios from "axios";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const timeRanges = [
  { value: "30", label: "30 Days" },
  { value: "90", label: "90 Days" },
  { value: "180", label: "6 Months" },
  { value: "365", label: "1 Year" },
];

const SignalIcon = ({ signal, size = "w-12 h-12" }) => {
  switch (signal) {
    case 'bullish': return <TrendingUp className={cn(size, "text-[#10B981]")} />;
    case 'bearish': return <TrendingDown className={cn(size, "text-[#F43F5E]")} />;
    default: return <MinusCircle className={cn(size, "text-[#6366F1]")} />;
  }
};

const signalColor = (signal) => {
  if (signal === 'bullish') return 'text-[#10B981]';
  if (signal === 'bearish') return 'text-[#F43F5E]';
  return 'text-[#6366F1]';
};

export default function Predictions() {
  const [cities, setCities] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [selectedCity, setSelectedCity] = useState("");
  const [selectedSector, setSelectedSector] = useState("");
  const [selectedStock, setSelectedStock] = useState("");
  const [timeRange, setTimeRange] = useState("90");
  const [prediction, setPrediction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [availableStocks, setAvailableStocks] = useState([]); // [{symbol, name}]

  // Top-5 scan mode
  const [scanMode, setScanMode] = useState(false); // true = scan all, false = pick one
  const [topPredictions, setTopPredictions] = useState([]);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    if (selectedSector) fetchSectorStocks(selectedSector);
  }, [selectedSector]);

  const fetchSectorStocks = async (sector) => {
    try {
      const res = await axios.get(`${API}/sector-symbols/${sector}`);
      const symbols = res.data.symbols || [];
      // Build [{symbol, name}] — fetch names from yfinance info if available, else just ticker
      const stockList = symbols.map(sym => ({
        symbol: sym,
        name: sym.replace('.AX', '')
      }));
      setAvailableStocks(stockList);
      if (stockList.length > 0) setSelectedStock(stockList[0].symbol);
      else setSelectedStock("");
    } catch (err) {
      console.error("Failed to fetch sector stocks:", err);
      setAvailableStocks([]);
      setSelectedStock("");
    }
  };

  const fetchInitialData = async () => {
    try {
      const [citiesRes, sectorsRes] = await Promise.all([
        axios.get(`${API}/cities`),
        axios.get(`${API}/sectors`)
      ]);
      setCities(citiesRes.data);
      const realSectors = sectorsRes.data.filter(s => s.key !== 'all');
      setSectors(realSectors);
      if (citiesRes.data.length > 0) setSelectedCity(citiesRes.data[0].key);
      if (realSectors.length > 0) setSelectedSector(realSectors[0].key);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    }
  };

  const fetchPrediction = useCallback(async () => {
    if (!selectedCity || !selectedStock) return;
    setLoading(true);
    setPrediction(null);
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      const res = await axios.get(`${API}/prediction-signal/${selectedCity}/${selectedStock}`, {
        params: {
          start_date: startDate.toISOString().split('T')[0],
          end_date: endDate.toISOString().split('T')[0]
        }
      });
      setPrediction(res.data);
    } catch (err) {
      console.error("Failed to fetch prediction:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedCity, selectedStock, timeRange]);

  // Scan top N stocks in sector and rank by prediction confidence
  const handleScanAll = async () => {
    if (!selectedCity || !selectedSector) return;
    setScanLoading(true);
    setScanError(null);
    setTopPredictions([]);
    try {
      // First get sector stocks (limit to 20 for speed — predictable response time)
      const symbolsRes = await axios.get(`${API}/sector-symbols/${selectedSector}`);
      const symbols = (symbolsRes.data.symbols || []).slice(0, 20);

      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - parseInt(timeRange));
      const dateParams = {
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0]
      };

      // Run predictions in batches of 5 to avoid overwhelming the server
      const results = [];
      for (let i = 0; i < symbols.length; i += 5) {
        const batch = symbols.slice(i, i + 5);
        const batchResults = await Promise.allSettled(
          batch.map(sym =>
            axios.get(`${API}/prediction-signal/${selectedCity}/${sym}`, { params: dateParams })
              .then(r => ({ symbol: sym, name: sym.replace('.AX', ''), ...r.data }))
          )
        );
        batchResults.forEach(r => {
          if (r.status === 'fulfilled' && r.value?.signal) results.push(r.value);
        });
      }

      // Sort: bullish first, then by confidence desc
      results.sort((a, b) => {
        const order = { bullish: 0, neutral: 1, bearish: 2 };
        const oDiff = (order[a.signal] ?? 1) - (order[b.signal] ?? 1);
        if (oDiff !== 0) return oDiff;
        return (b.confidence || 0) - (a.confidence || 0);
      });

      setTopPredictions(results.slice(0, 5));
      if (results.length === 0) setScanError("No prediction data returned. Try a different sector or city.");
    } catch (err) {
      console.error("Scan failed:", err);
      setScanError("Scan failed. Please try again.");
    } finally {
      setScanLoading(false);
    }
  };

  const getCityName = () => cities.find(c => c.key === selectedCity)?.name || selectedCity;
  const getSectorName = () => sectors.find(s => s.key === selectedSector)?.name || selectedSector;
  const getStockDisplay = (sym) => sym?.replace('.AX', '') || sym;

  return (
    <div className="p-8" data-testid="predictions-page">
      {/* Header */}
      <header className="mb-6">
        <h1 className="font-heading text-4xl font-black text-white mb-2">Investment Predictions</h1>
        <p className="text-[#A1A1AA]">Generate signals based on weather-stock pattern matching</p>
      </header>

      {/* Disclaimer */}
      <div className="bento-card mb-6 flex items-start gap-3 bg-[#F59E0B]/5 border-[#F59E0B]/20">
        <AlertTriangle className="w-5 h-5 text-[#F59E0B] shrink-0 mt-0.5" />
        <p className="text-sm text-[#A1A1AA]">
          <span className="text-[#F59E0B] font-medium">Experimental Tool — </span>
          For educational purposes only. Weather-stock correlations do not imply causation and should not be the sole basis for investment decisions. Always consult a financial advisor.
        </p>
      </div>

      {/* Mode toggle */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => { setScanMode(false); setTopPredictions([]); }}
          className={cn("px-4 py-2 text-sm font-medium rounded transition-colors",
            !scanMode ? "bg-[#38BDF8] text-black" : "bg-[#18181B] text-[#A1A1AA] hover:text-white border border-[#27272A]"
          )}
        >
          <Zap className="w-4 h-4 inline mr-1.5" />Analyse One Stock
        </button>
        <button
          onClick={() => { setScanMode(true); setPrediction(null); }}
          className={cn("px-4 py-2 text-sm font-medium rounded transition-colors",
            scanMode ? "bg-[#22c55e] text-black" : "bg-[#18181B] text-[#A1A1AA] hover:text-white border border-[#27272A]"
          )}
        >
          <Search className="w-4 h-4 inline mr-1.5" />Find Top 5 in Sector
        </button>
      </div>

      {/* Controls */}
      <div className="bento-card mb-8">
        <h2 className="font-heading text-lg font-bold text-white mb-4">Analysis Parameters</h2>
        <div className={cn("grid gap-4 mb-6", scanMode ? "grid-cols-1 md:grid-cols-3" : "grid-cols-1 md:grid-cols-2 lg:grid-cols-4")}>
          {/* City */}
          <div>
            <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">City</label>
            <Select value={selectedCity} onValueChange={setSelectedCity}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A]">
                <MapPin className="w-4 h-4 mr-2" /><SelectValue placeholder="Select City" />
              </SelectTrigger>
              <SelectContent>
                {cities.map(city => (
                  <SelectItem key={city.key} value={city.key}>{city.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Sector */}
          <div>
            <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">Sector</label>
            <Select value={selectedSector} onValueChange={setSelectedSector}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A]">
                <BarChart3 className="w-4 h-4 mr-2" /><SelectValue placeholder="Select Sector" />
              </SelectTrigger>
              <SelectContent>
                {sectors.map(sector => (
                  <SelectItem key={sector.key} value={sector.key}>
                    {sector.name} ({sector.stock_count} stocks)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Stock picker — only in single mode */}
          {!scanMode && (
            <div>
              <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">Stock</label>
              <Select value={selectedStock} onValueChange={setSelectedStock}>
                <SelectTrigger className="bg-[#09090B] border-[#27272A]">
                  <TrendingUp className="w-4 h-4 mr-2" /><SelectValue placeholder="Select Stock" />
                </SelectTrigger>
                <SelectContent>
                  {availableStocks.map(s => (
                    <SelectItem key={s.symbol} value={s.symbol}>
                      {getStockDisplay(s.symbol)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Time range */}
          <div>
            <label className="text-xs text-[#A1A1AA] uppercase tracking-wider mb-2 block">Time Range</label>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="bg-[#09090B] border-[#27272A]">
                <Calendar className="w-4 h-4 mr-2" /><SelectValue />
              </SelectTrigger>
              <SelectContent>
                {timeRanges.map(r => (
                  <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {!scanMode ? (
          <Button
            onClick={fetchPrediction}
            disabled={loading || !selectedCity || !selectedStock}
            className="w-full md:w-auto bg-[#38BDF8] hover:bg-[#0EA5E9] text-black font-medium"
          >
            {loading ? <><span className="animate-spin mr-2">⏳</span>Analysing...</> : <><Lightbulb className="w-4 h-4 mr-2" />Generate Prediction Signal</>}
          </Button>
        ) : (
          <Button
            onClick={handleScanAll}
            disabled={scanLoading || !selectedCity || !selectedSector}
            className="w-full md:w-auto bg-[#22c55e] hover:bg-[#16a34a] text-black font-bold"
          >
            {scanLoading
              ? <><span className="animate-spin mr-2">⏳</span>Scanning top 20 stocks...</>
              : <><Search className="w-4 h-4 mr-2" />Find Top 5 Predictions in {getSectorName()}</>
            }
          </Button>
        )}
      </div>

      {/* SINGLE STOCK RESULT */}
      {!loading && prediction && !scanMode && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          {/* Signal Banner */}
          <div className={cn("bento-card border-2 flex items-center gap-6 p-6",
            prediction.signal === 'bullish' && 'border-[#10B981] bg-[#10B981]/5',
            prediction.signal === 'bearish' && 'border-[#F43F5E] bg-[#F43F5E]/5',
            prediction.signal === 'neutral' && 'border-[#6366F1] bg-[#6366F1]/5'
          )}>
            <SignalIcon signal={prediction.signal} />
            <div className="flex-1">
              <h3 className={cn("font-heading text-3xl font-black uppercase", signalColor(prediction.signal))}>
                {prediction.signal}
              </h3>
              <p className="text-[#A1A1AA] text-sm mt-1">
                {getStockDisplay(selectedStock)} · {getCityName()}
              </p>
            </div>
            <div className="text-right">
              <span className="text-xs text-[#52525B]">Confidence</span>
              <p className="font-data text-2xl font-bold text-white">{prediction.confidence?.toFixed(1)}%</p>
              <div className="mt-2 w-32 h-2 bg-[#27272A] overflow-hidden">
                <div className={cn("h-full transition-all duration-500",
                  prediction.signal === 'bullish' && 'bg-[#10B981]',
                  prediction.signal === 'bearish' && 'bg-[#F43F5E]',
                  prediction.signal === 'neutral' && 'bg-[#6366F1]'
                )} style={{ width: `${prediction.confidence || 0}%` }} />
              </div>
            </div>
          </div>

          {/* Detail stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: "Correlation", value: prediction.correlation?.correlation?.toFixed(3) || '-', colored: true, val: prediction.correlation?.correlation },
              { label: "P-Value", value: prediction.correlation?.p_value?.toFixed(4) || '-' },
              { label: "Temp Trend", value: prediction.temperature_trend || '-', capitalize: true },
              { label: "Data Points", value: prediction.data_points_analyzed || '-' },
            ].map(({ label, value, colored, val, capitalize }) => (
              <div key={label} className="bento-card">
                <p className="text-xs text-[#A1A1AA] uppercase mb-1">{label}</p>
                <p className={cn("font-data text-xl font-bold", colored ? (val > 0 ? 'text-[#10B981]' : 'text-[#F43F5E]') : 'text-white', capitalize && 'capitalize')}>
                  {value}
                </p>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bento-card">
              <h3 className="font-heading text-lg font-bold text-white mb-3">Analysis Rationale</h3>
              <p className="text-[#A1A1AA]">{prediction.rationale}</p>
            </div>
            <div className="bento-card">
              <h3 className="font-heading text-sm font-bold text-[#A1A1AA] uppercase mb-3">Correlation Details</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-[#52525B] mb-1">Strength</p>
                  <span className={cn("px-3 py-1 text-xs font-data uppercase border",
                    prediction.correlation?.strength === 'strong' && 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]',
                    prediction.correlation?.strength === 'moderate' && 'bg-[#F59E0B]/20 text-[#F59E0B] border-[#F59E0B]',
                    prediction.correlation?.strength === 'weak' && 'bg-[#6366F1]/20 text-[#6366F1] border-[#6366F1]',
                    prediction.correlation?.strength === 'negligible' && 'bg-[#52525B]/20 text-[#52525B] border-[#52525B]'
                  )}>
                    {prediction.correlation?.strength || 'Unknown'}
                  </span>
                </div>
                <div>
                  <p className="text-xs text-[#52525B] mb-1">Direction</p>
                  <span className={cn("px-3 py-1 text-xs font-data uppercase border",
                    prediction.correlation?.direction === 'positive' && 'bg-[#10B981]/20 text-[#10B981] border-[#10B981]',
                    prediction.correlation?.direction === 'negative' && 'bg-[#F43F5E]/20 text-[#F43F5E] border-[#F43F5E]'
                  )}>
                    {prediction.correlation?.direction || 'Unknown'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* SCAN MODE RESULTS - Top 5 */}
      {scanMode && (
        <div>
          {scanLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-32 bg-[#18181B] skeleton-pulse rounded" />
              ))}
            </div>
          )}

          {scanError && !scanLoading && (
            <div className="text-center py-12 text-[#A1A1AA]">{scanError}</div>
          )}

          {!scanLoading && topPredictions.length > 0 && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <h2 className="font-heading text-xl font-bold text-white mb-1">
                Top 5 Predictions · {getSectorName()} · {getCityName()}
              </h2>
              <p className="text-sm text-[#52525B] mb-6">Ranked by signal strength and confidence · scanned top 20 stocks</p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {topPredictions.map((pred, idx) => (
                  <motion.div
                    key={pred.symbol}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className={cn("bento-card border-2",
                      pred.signal === 'bullish' && 'border-[#10B981]',
                      pred.signal === 'bearish' && 'border-[#F43F5E]',
                      pred.signal === 'neutral' && 'border-[#27272A]',
                      idx === 0 && 'ring-2 ring-offset-2 ring-offset-[#09090B] ring-[#22c55e]'
                    )}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-[10px] text-[#52525B] uppercase tracking-wider mb-0.5">
                          {idx === 0 ? '🏆 Best Pick' : `#${idx + 1}`}
                        </p>
                        <p className="font-data text-xl font-bold text-white">{getStockDisplay(pred.symbol)}</p>
                      </div>
                      <SignalIcon signal={pred.signal} size="w-8 h-8" />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={cn("text-sm font-bold uppercase", signalColor(pred.signal))}>
                        {pred.signal}
                      </span>
                      <span className="font-data text-sm text-white">
                        {pred.confidence?.toFixed(1)}% confidence
                      </span>
                    </div>
                    <div className="mt-2 h-1.5 bg-[#27272A] overflow-hidden rounded">
                      <div className={cn("h-full",
                        pred.signal === 'bullish' && 'bg-[#10B981]',
                        pred.signal === 'bearish' && 'bg-[#F43F5E]',
                        pred.signal === 'neutral' && 'bg-[#6366F1]'
                      )} style={{ width: `${pred.confidence || 0}%` }} />
                    </div>
                    <p className="text-xs text-[#52525B] mt-2">{pred.rationale}</p>
                    <button
                      onClick={() => { setScanMode(false); setSelectedStock(pred.symbol); setTimeout(fetchPrediction, 100); }}
                      className="mt-3 text-xs text-[#38BDF8] hover:text-white transition-colors"
                    >
                      View full analysis →
                    </button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {!scanLoading && topPredictions.length === 0 && !scanError && (
            <div className="text-center py-16">
              <Search className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
              <p className="text-[#A1A1AA] mb-2">Select a city and sector, then click Find Top 5</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state - single mode */}
      {!loading && !prediction && !scanMode && (
        <div className="text-center py-16">
          <Lightbulb className="w-16 h-16 text-[#52525B] mx-auto mb-4" />
          <p className="text-[#A1A1AA] mb-2">No prediction generated yet</p>
          <p className="text-sm text-[#52525B]">Select your parameters above and click Generate</p>
        </div>
      )}

      {/* Methodology */}
      <div className="mt-12 bento-card">
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-[#38BDF8] shrink-0 mt-0.5" />
          <div>
            <h3 className="font-heading text-lg font-bold text-white mb-2">Methodology</h3>
            <p className="text-sm text-[#A1A1AA] mb-3">
              This prediction system analyses the Pearson correlation between daily temperature changes and stock price changes over the selected time period.
            </p>
            <ol className="text-sm text-[#52525B] space-y-1 list-decimal list-inside">
              <li>Calculating the correlation coefficient between weather and stock movements</li>
              <li>Determining the recent temperature trend (rising/falling over last 7 days)</li>
              <li>Combining correlation direction with temperature trend to generate a signal</li>
              <li>Confidence is based on the absolute correlation strength</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
